import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  createUserWithEmailAndPassword,
  onIdTokenChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  User,
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import { Customer, getMe } from '../api/CustomerClient';
import {
  FriendCodeResponse,
  PointsSummary,
  getMyFriendCode,
  getMyPoints,
} from '../api/PointsClient';

type CustomerContextValue = {
  // Firebase user — null when signed out
  user: User | null;
  // Our customer row — null until the first /me round-trip succeeds
  customer: Customer | null;
  // Loyalty balance + recent ledger entries; null until the first
  // /me/points round-trip succeeds.
  points: PointsSummary | null;
  // Owned friend code + first-purchase eligibility flag; null until the
  // first /me/friend-code round-trip succeeds.
  friendCode: FriendCodeResponse | null;
  // True until the initial Firebase auth state resolves on mount
  loading: boolean;
  // True while a customer fetch / sign-in is in flight
  customerLoading: boolean;
  // Last error encountered loading the customer profile (if any)
  customerError: string | null;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  signOutCustomer: () => Promise<void>;
  // Force a fresh idToken (Firebase normally refreshes automatically)
  idToken: () => Promise<string | null>;
  // Re-fetch the /me profile (use after PATCH)
  refreshCustomer: () => Promise<void>;
  // Re-fetch points balance + ledger (use after an order is placed so
  // the /account page reflects the new credit without a hard reload).
  refreshPoints: () => Promise<void>;
};

// Convert a Firebase auth error code into a customer-facing message. We
// flatten "wrong password" / "user not found" / "invalid credentials"
// into a single message so the wording doesn't leak whether an account
// exists (a tiny anti-enumeration measure).
export const friendlyAuthError = (err: any): string => {
  const code: string = err?.code ?? '';
  switch (code) {
    case 'auth/invalid-email':
      return 'That email address looks malformed.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
      return 'Email or password is incorrect.';
    case 'auth/email-already-in-use':
      return 'An account already exists for this email. Try signing in instead.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Try again in a few minutes.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return ''; // user-initiated, no error to show
    default:
      return err?.message || 'Something went wrong. Try again.';
  }
};

const CustomerContext = createContext<CustomerContextValue>({
  user: null,
  customer: null,
  points: null,
  friendCode: null,
  loading: true,
  customerLoading: false,
  customerError: null,
  signInWithGoogle: async () => {},
  signInWithEmail: async () => {},
  signUpWithEmail: async () => {},
  sendPasswordReset: async () => {},
  signOutCustomer: async () => {},
  idToken: async () => null,
  refreshCustomer: async () => {},
  refreshPoints: async () => {},
});

type Props = { children: React.ReactNode };

export const CustomerProvider: React.FC<Props> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [points, setPoints] = useState<PointsSummary | null>(null);
  const [friendCode, setFriendCode] = useState<FriendCodeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerError, setCustomerError] = useState<string | null>(null);

  // Cache the latest token in a ref so idToken() can return synchronously
  // when fresh, and only call Firebase when we don't have one yet.
  const tokenRef = useRef<string | null>(null);

  const fetchCustomer = useCallback(async (token: string) => {
    setCustomerLoading(true);
    setCustomerError(null);
    try {
      // Fan out the three customer reads in parallel — they're
      // independent and all live on the same server, so serializing
      // would just stack network latency.
      const [me, pts, fc] = await Promise.all([
        getMe(token),
        getMyPoints(token).catch((e) => {
          console.warn('[customer] /me/points failed (non-fatal):', e);
          return null;
        }),
        getMyFriendCode(token).catch((e) => {
          console.warn('[customer] /me/friend-code failed (non-fatal):', e);
          return null;
        }),
      ]);
      setCustomer(me);
      setPoints(pts);
      setFriendCode(fc);
    } catch (err: any) {
      // 401 here means the token is bad; surface it but don't sign out — the
      // user might just need to retry. Other errors are reported as-is.
      const msg = err?.response?.data ?? err?.message ?? String(err);
      console.error('[customer] /me fetch failed:', msg);
      setCustomerError(typeof msg === 'string' ? msg : 'Failed to load profile');
      setCustomer(null);
      setPoints(null);
      setFriendCode(null);
    } finally {
      setCustomerLoading(false);
    }
  }, []);

  useEffect(() => {
    // onIdTokenChanged fires on sign-in, sign-out, AND the periodic refresh
    // Firebase does every ~55 min — so we always hold a fresh token.
    return onIdTokenChanged(auth, async (u) => {
      if (u) {
        const token = await u.getIdToken();
        tokenRef.current = token;
        setUser(u);
        await fetchCustomer(token);
      } else {
        tokenRef.current = null;
        setUser(null);
        setCustomer(null);
        setPoints(null);
        setFriendCode(null);
        setCustomerError(null);
      }
      setLoading(false);
    });
  }, [fetchCustomer]);

  const signInWithGoogle = useCallback(async () => {
    await signInWithPopup(auth, googleProvider);
    // onIdTokenChanged handler picks up the result.
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  // Creates the account and best-effort fires off a verification email.
  // We don't gate access on verification yet; email_verified flows through
  // to our customers row via the Firebase ID token claim.
  const signUpWithEmail = useCallback(async (email: string, password: string) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    try {
      await sendEmailVerification(cred.user);
    } catch (err) {
      console.warn('[customer] sendEmailVerification failed (non-fatal):', err);
    }
  }, []);

  const sendPasswordReset = useCallback(async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  }, []);

  const signOutCustomer = useCallback(async () => {
    await fbSignOut(auth);
  }, []);

  const idToken = useCallback(async () => {
    if (!auth.currentUser) return null;
    // forceRefresh=false — let Firebase decide whether to use the cache.
    const t = await auth.currentUser.getIdToken();
    tokenRef.current = t;
    return t;
  }, []);

  const refreshCustomer = useCallback(async () => {
    const t = await idToken();
    if (t) await fetchCustomer(t);
  }, [idToken, fetchCustomer]);

  const refreshPoints = useCallback(async () => {
    const t = await idToken();
    if (!t) return;
    try {
      // Both endpoints contribute to the points-side UI (friend code's
      // first_purchase flag flips after the first paid order), so refresh
      // them together.
      const [pts, fc] = await Promise.all([
        getMyPoints(t),
        getMyFriendCode(t),
      ]);
      setPoints(pts);
      setFriendCode(fc);
    } catch (err) {
      console.warn('[customer] refreshPoints failed (non-fatal):', err);
    }
  }, [idToken]);

  const value = useMemo<CustomerContextValue>(
    () => ({
      user,
      customer,
      points,
      friendCode,
      loading,
      customerLoading,
      customerError,
      signInWithGoogle,
      signInWithEmail,
      signUpWithEmail,
      sendPasswordReset,
      signOutCustomer,
      idToken,
      refreshCustomer,
      refreshPoints,
    }),
    [
      user,
      customer,
      points,
      friendCode,
      loading,
      customerLoading,
      customerError,
      signInWithGoogle,
      signInWithEmail,
      signUpWithEmail,
      sendPasswordReset,
      signOutCustomer,
      idToken,
      refreshCustomer,
      refreshPoints,
    ],
  );

  return <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>;
};

export const useCustomer = () => useContext(CustomerContext);
